/**
 * Venue booking (full-day) — choose venue, pick a date from the next 30 days
 * (busyDates from the API disabled, dates beyond the venue's advanceDays
 * disabled), state purpose, submit → PENDING/APPROVED banner.
 * Wired: GET /api/bookings/venues, POST /api/bookings/venues/<id>/book.
 * Port of design/source/screens-account.jsx VenueBooking.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import {
  backOr, BottomBar, CardSkeletons, ChipsRow, DateChip, dateParts, inr, isoDateAt, venueIcon, venuePillStatus,
} from '@/components/money/bits';
import {
  bookVenue, errorMessage, fetchVenues, isConflict, useAsync, type Venue, type VenueBookingStatus,
} from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Field, IconButton, Input, Pill, Screen, TopBar } from '@/ui';

const HORIZON_DAYS = 30;

export default function VenueBookingScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data: venues, loading, error, refetch } = useAsync(fetchVenues, []);

  const [venueIdx, setVenueIdx] = React.useState(0);
  const [date, setDate] = React.useState<string | null>(null);
  const [purpose, setPurpose] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<VenueBookingStatus | null>(null);

  const venue: Venue | undefined = venues?.[venueIdx];
  const today0 = React.useMemo(() => isoDateAt(0), []);
  const days = React.useMemo(() => Array.from({ length: HORIZON_DAYS }, (_, i) => isoDateAt(i + 1)), []);

  const submit = async () => {
    if (!venue || !date || !purpose.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await bookVenue(venue.id, date, purpose.trim());
      setResult(res.status);
      setDate(null);
      setPurpose('');
    } catch (e) {
      if (isConflict(e)) {
        setSubmitError(t('vb_conflict'));
        void refetch();
      } else {
        setSubmitError(errorMessage(e, t('vb_submit_error')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('vb_title')}
        onBack={backOr('/(tabs)/home')}
        right={
          <IconButton label={t('am_my_bookings')} onPress={() => router.push('/my-bookings')}>
            <Icon name="calendar" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <CardSkeletons count={2} />
        </Screen>
      ) : error ? (
        <Screen padded>
          <EmptyState
            title={t('vb_err_title')}
            body={t('vb_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : !venues || venues.length === 0 ? (
        <Screen padded>
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={t('vb_empty_title')}
            body={t('vb_empty_body')}
          />
        </Screen>
      ) : result ? (
        <Screen padded style={{ paddingTop: space.s6, alignItems: 'center' }}>
          <Icon name={result === 'APPROVED' ? 'check-circle' : 'clock'} size={48} color={colors.primary500} />
          <AppText variant="headingSm" weight="500" style={{ marginTop: space.s4, textAlign: 'center' }}>
            {result === 'APPROVED' ? t('vb_booked') : t('vb_submitted')}
          </AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 6, textAlign: 'center', maxWidth: 300 }}>
            {result === 'APPROVED' ? t('vb_confirmed') : t('vb_pending')}
          </AppText>
          <View style={{ marginTop: space.s3 }}>
            <Pill status={venuePillStatus(result)} />
          </View>
          <Button title={t('vb_book_another')} kind="secondary" style={{ marginTop: space.s6 }} onPress={() => setResult(null)} />
          <Button title={t('am_view_bookings')} kind="tertiary" style={{ marginTop: space.s2 }} onPress={() => router.push('/my-bookings')} />
        </Screen>
      ) : (
        <>
          <Screen padded style={{ paddingTop: space.s4 }}>
            <AppText variant="label" tone="tertiary" style={{ marginBottom: 10 }}>{t('vb_choose_venue')}</AppText>
            <View style={{ gap: space.s2 }}>
              {venues.map((v, i) => {
                const active = i === venueIdx;
                return (
                  <Pressable
                    key={v.id}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => setVenueIdx(i)}
                    style={{
                      flexDirection: 'row', alignItems: 'center', gap: space.s3, padding: space.s3, minHeight: 44,
                      borderRadius: radius.lg, borderWidth: active ? 1.5 : StyleSheet.hairlineWidth,
                      borderColor: active ? colors.primary500 : colors.borderSoft,
                      backgroundColor: active ? colors.primary50 : colors.canvas0,
                    }}
                  >
                    <View style={{ width: 44, height: 44, borderRadius: radius.lg, backgroundColor: colors.canvas1, alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name={venueIcon(v.name)} size={20} color={colors.textSecondary} />
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="500">{v.name}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {t('vb_up_to', { n: v.advanceDays })} · {inr(v.fee)}
                      </AppText>
                    </View>
                    {active ? <Icon name="check-circle" size={18} color={colors.primary500} /> : null}
                  </Pressable>
                );
              })}
            </View>

            {venue ? (
              <>
                <AppText variant="label" tone="tertiary" style={{ marginTop: space.s6, marginBottom: 10 }}>{t('vb_pick_day')}</AppText>
                <View style={{ marginHorizontal: -space.s4 }}>
                  <ChipsRow>
                    {days.map((iso) => {
                      const { weekday, day } = dateParts(iso);
                      const daysAhead = Math.round((new Date(iso).getTime() - new Date(today0).getTime()) / 86400000);
                      const disabled = venue.busyDates.includes(iso) || daysAhead > venue.advanceDays;
                      return (
                        <DateChip
                          key={iso}
                          weekday={weekday}
                          day={day}
                          selected={date === iso}
                          disabled={disabled}
                          onPress={() => setDate(iso)}
                        />
                      );
                    })}
                  </ChipsRow>
                </View>

                <Field label={t('vb_purpose')} help={t('vb_purpose_help')} >
                  <Input
                    value={purpose}
                    onChangeText={setPurpose}
                    placeholder={t('vb_purpose_ph')}
                    multiline
                    style={{ minHeight: 72, textAlignVertical: 'top', paddingTop: 10, marginTop: space.s4 }}
                  />
                </Field>
              </>
            ) : null}

            <View style={{ flexDirection: 'row', gap: space.s2, backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s3, marginTop: space.s4 }}>
              <Icon name="info" size={15} color={colors.info500} />
              <AppText variant="bodySm" tone="secondary" style={{ flex: 1 }}>
                {t('vb_info')}
              </AppText>
            </View>
            {submitError ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{submitError}</AppText> : null}
          </Screen>
          <BottomBar>
            <Button
              title={t('vb_request')}
              size="lg"
              disabled={!date || !purpose.trim()}
              loading={submitting}
              onPress={() => void submit()}
            />
          </BottomBar>
        </>
      )}
    </Screen>
  );
}
